import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    title: string;
    appLabel: string;
    appIconAlt: string;
    connectTitle: string;
    connectSubtitle: string;
    connectSourceLabel: string;
    connectDestinationLabel: string;
    connectDestinationIconAlt: string;
    connectLearnMore: string;
    connect: string;
    brandLabel: string;
    brandPlaceholder: string;
    brandLoadingPlaceholder: string;
    categoryLabel: string;
    categoryPlaceholder: string;
    categoryLoadingPlaceholder: string;
    description: string;
    learnMore: string;
    run: string;
    dialogCloseLabel: string;
    loadError: string;
    categoryLoadError: string;
    emptyMessage: string;
    categoryEmptyMessage: string;
    createBrandLinkText: string;
    validationWarningSummary: ({ failedCount, totalCount }: {
        failedCount: number;
        totalCount: number;
    }) => string;
    validationWarningIntro: string;
    validationWarningContinue: string;
    unsupportedFormatReason: (mimeType: string) => string;
    missingMetadataReason: string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Title of the brand validation dialog. */
            title: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.title?c=2r05gnxhbp73y", defaultMessage: "Run Brand Validation" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Source app label shown beneath the title. */
            appLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.appLabel?c=v1np1os67mmk", defaultMessage: "Powered by Adobe Brand Intelligence" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Alt text for the app icon image in the dialog header. */
            appIconAlt: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.appIconAlt?c=v1np1os67mmk", defaultMessage: "Powered by Adobe Brand Intelligence" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title shown on the Adobe IMS connect step (before the user is signed in). */
            connectTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.connectTitle?c=q2cjdnb43kzn", defaultMessage: "Connect Adobe Brand Services" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Subtitle shown on the Adobe IMS connect step. */
            connectSubtitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.connectSubtitle?c=109ckklda1quj", defaultMessage: "To use this action, you must connect your Frame.io account with Adobe Brand Services." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label under the Frame.io source icon on the connect step. */
            connectSourceLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.connectSourceLabel?c=ta6kd3l5jt87", defaultMessage: "Frame.io" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label under the destination icon on the connect step. */
            connectDestinationLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.connectDestinationLabel?c=2ml0bpsftx4zy", defaultMessage: "Adobe Brand Services" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Alt text for the destination icon on the connect step. */
            connectDestinationIconAlt: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.connectDestinationIconAlt?c=2ml0bpsftx4zy", defaultMessage: "Adobe Brand Services" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for the Learn More link on the connect step. */
            connectLearnMore: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.connectLearnMore?c=h8sd9u7vqbji", defaultMessage: "Learn More" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Connect button label on the connect step. */
            connect: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.connect?c=3pkcv7gk4h5hp", defaultMessage: "Connect" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label above the brand select. */
            brandLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.brandLabel?c=hiwzif8r4jls", defaultMessage: "Brand" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Placeholder shown in the select before a brand is chosen. */
            brandPlaceholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.brandPlaceholder?c=z8yqvpehoimc", defaultMessage: "Select a brand" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Placeholder shown while brands are being fetched. */
            brandLoadingPlaceholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.brandLoadingPlaceholder?c=16xj71ghsrpc4", defaultMessage: "Loading brands\u2026" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label above the category select. */
            categoryLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.categoryLabel?c=3cmdl2bty0au8", defaultMessage: "Category" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Placeholder shown in the category select before a category is chosen. */
            categoryPlaceholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.categoryPlaceholder?c=17q7t1nlafiwb", defaultMessage: "Select a category" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Placeholder shown while categories are being fetched. */
            categoryLoadingPlaceholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.categoryLoadingPlaceholder?c=fmgjonf3o7p9", defaultMessage: "Loading categories\u2026" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Description of what the agent does when run. */
            description: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.description?c=x3l50rcgp1nb", defaultMessage: "The Brand Validation agent will leave feedback as comments on selected assets." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Inline link text after the description, pointing to docs. */
            learnMore: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.learnMore?c=2bu5tw74q9bww", defaultMessage: "Learn more" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Run button label. */
            run: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.run?c=38f5qtk8o6w3d", defaultMessage: "Run" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Accessible label used to close the dialog. */
            dialogCloseLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.dialogCloseLabel?c=2injcj593oazc", defaultMessage: "Close dialog" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error message shown when the brand list fails to load. */
            loadError: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.loadError?c=1ywra8wdr7xns", defaultMessage: "We couldn\u2019t load your brands. Please try again." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error message shown when the category list fails to load. */
            categoryLoadError: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.categoryLoadError?c=2fdhofq6ai2dh", defaultMessage: "We couldn\u2019t load categories. Please try again." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message shown when the account has no brands yet. */
            emptyMessage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.emptyMessage?c=1qml7j0vkuvwf", defaultMessage: "No brands are available for this account." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message shown when the selected brand has no categories. */
            categoryEmptyMessage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.categoryEmptyMessage?c=2jslat4i5b64w", defaultMessage: "No categories are available for this brand." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Call-to-action text linking out to the brand portal. */
            createBrandLinkText: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.createBrandLinkText?c=22p10s20fmmzi", defaultMessage: "Create a brand in the brand portal" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Summary text for the unsupported-files accordion header. */
            validationWarningSummary: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ failedCount, totalCount }: {
                        failedCount: number;
                        totalCount: number;
                    }): string => __intl__.intl().formatMessage({ id: "UbsValidateDialog.validationWarningSummary?c=1vtmkagsm8rrd", defaultMessage: "{failedCount} of {totalCount} files have unsupported formats" }, { failedCount: failedCount, totalCount: totalCount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Introduction text shown when the unsupported-files accordion is expanded. */
            validationWarningIntro: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.validationWarningIntro?c=p5tnjbg54uv", defaultMessage: "The following files will not be included in validation:" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Continuation text shown when some files are still valid. */
            validationWarningContinue: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.validationWarningContinue?c=30amoigt46sv1", defaultMessage: "You can still run validation for the remaining files." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Reason label shown for an asset whose MIME type isn't allowed. */
            unsupportedFormatReason: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (mimeType: string): string => __intl__.intl().formatMessage({ id: "UbsValidateDialog.unsupportedFormatReason?c=2dyw30qnqrk8l", defaultMessage: "Unsupported format: {mimeType}" }, { mimeType: mimeType }));
                },
                enumerable: true,
                configurable: true
            },
            /** Reason label shown when an asset's MIME type metadata isn't available. */
            missingMetadataReason: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "UbsValidateDialog.missingMetadataReason?c=3hzwk83xzjo3l", defaultMessage: "Metadata unavailable" }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
